import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Container, Row, Col, Form, Button, Table, InputGroup } from 'react-bootstrap';
import toast from 'react-hot-toast';
import ContextHelper from '../../ContextHooks/ContextHelper';
import { getDataFromServer, postDatatoServer } from '../../utils/apiBase';
import moment from 'moment/moment';
import PatientRegistrationModal from './PatientRegistrationModal';
import SearchableDropdown from './SearchableDropdown';
import BillingReports from './BillingReports';
import { Add, Delete } from '@mui/icons-material';
import logoInvoice from '../../Assets/sid.png';
import { FormField } from '../formFields/FormField';

const useDebounce = (func, delay) => {
  const timeoutRef = useRef(null);

  return useCallback(
    (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        func(...args);
      }, delay);
    },
    [func, delay]
  );
};

const PatientBilling = () => {
  const { currentUser } = ContextHelper();
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [show, setShow] = useState(false);
  const [base64Image, setBase64Image] = useState('');
  const getCurrentTime = () => {
    const now = new Date();
    const formattedTime =
      String(now.getHours()).padStart(2, '0') +
      ':' +
      String(now.getMinutes()).padStart(2, '0') +
      ':' +
      String(now.getSeconds()).padStart(2, '0');

    return formattedTime;
  };
  const currentTime = getCurrentTime();

  const [formData, setFormData] = useState({
    emergency: false,
    dateOfEmergency: '',
    patientName: '',
    tenant: '',
    scheme: '',
    contrast: '',
    schemeCode: '',
    payMode: '',
    remarks: '',
    dateOfBilling: new Date().toISOString().split('T')[0],
    mobileNumber: '',
    referringPhysicianName: '',
    studies: [{ study: null, studyDescription: '', studyId: '', amount: '', discount: '' }],
  });

  const [errors, setErrors] = useState({});
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [billingView, setBillingView] = useState(false);
  const [patientUsers, setPatientUsers] = useState([]);
  const [studyData, setStudyData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [billId, setBillId] = useState('');
  const [print, setPrint] = useState(false);

  useEffect(() => {
    fetchStudytData();
  }, []);

  const fetchPatientData = useCallback(
    (value) => {
      const ApiUrl = 'getPatients';

      const handleResponse = (res) => {
        if (res.status === 'success') {
          setPatientUsers(res.response);
        } else {
          toast.error(res.message || 'Failed to fetch patients.');
        }
      };

      const queryParams = new URLSearchParams();

      if (formData.emergency) {
        queryParams.append('date', value);
      } else {
        queryParams.append('mobile', value);
      }

      const params = {
        end_point: `${ApiUrl}?${queryParams.toString()}`,
        params: { token: currentUser?.token },
        call_back: handleResponse,
        props: '',
      };

      getDataFromServer(params).catch((error) => {
        console.error('Error fetching patient data:', error);
        toast.error('Failed to fetch patient data.');
      });
    },
    [currentUser, formData.emergency]
  );

  useEffect(() => {
    if (formData.emergency && formData.dateOfEmergency) {
      fetchPatientData(formData.dateOfEmergency);
    } else if (!formData.emergency && formData.mobileNumber.length === 10) {
      fetchPatientData(formData.mobileNumber);
    }
  }, [formData.emergency, formData.dateOfEmergency, formData.mobileNumber, fetchPatientData]);

  const debouncedFetchPatientData = useDebounce(fetchPatientData, 300);

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setFormData((prev) => ({
        ...prev,
        mobileNumber: value,
      }));

      if (value.length === 10) {
        debouncedFetchPatientData(value);
      }
    }
  };

  const fetchStudytData = () => {
    const ApiUrl = 'get-hospital-configs';

    const handleResponse = (res) => {
      if (res.status === 'success') {
        const transformedStudyData = transformPricingData(res.response);
        setStudyData(transformedStudyData);
      } else {
        toast.error(res.message || 'Failed to fetch studies.');
      }
    };

    const params = {
      end_point: ApiUrl,
      params: { token: currentUser?.token },
      call_back: handleResponse,
      props: '',
    };

    getDataFromServer(params).catch((error) => {
      console.error('Error fetching study data:', error);
      toast.error('Failed to fetch study data.');
    });
  };

  function transformPricingData(pricingData) {
    const studyDataArray = [];
    pricingData.forEach((obj) => {
      studyDataArray.push({
        id: obj.id,
        code: obj.Modality,
        description: obj.studyName,
        contrast: obj.contrast,
        regularPrice: obj.price,
        pvtPrice: obj.pvtPrice,
      });
    });

    return studyDataArray;
  }

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      studies: [{ study: null, studyDescription: '', studyId: '', amount: '', discount: '' }],
    }));
  }, [formData.tenant, formData.contrast]);

  const getFilteredStudies = () => {
    if (!studyData) return [];

    return studyData.filter((study) => study.contrast === (formData.contrast === 'Contrast'));
  };

  const displayStudy = (study) => {
    if (!study) return '';

    let priceDisplay;
    if (formData.tenant === 'Private') {
      priceDisplay = `₹${study.pvtPrice}`;
    } else {
      priceDisplay = `₹${study.regularPrice}`;
    }

    return `${study.description} (${study.code}) - ${priceDisplay}`;
  };

  const handleStudySelect = (study, index) => {
    const selectedPrice = formData.tenant === 'Private' ? study.pvtPrice : study.regularPrice;

    setFormData((prev) => ({
      ...prev,
      studies: prev.studies.map((s, i) =>
        i === index
          ? {
              ...s,
              study,
              amount: selectedPrice.toString(),
            }
          : s
      ),
    }));
  };

  const disabledSchemes = ['AYUSHMAN BHARAT', 'BPL', 'Deemed Free', 'Prison Patient'];
  const displayPatient = (patient) => `${patient.firstName} ${patient.lastName}`;

  const handleChange = (e, index) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      if (name.startsWith('studies.')) {
        const [, field] = name.split('.');
        return {
          ...prev,
          studies: prev.studies.map((study, i) => (i === index ? { ...study, [field]: value } : study)),
        };
      } else {
        return { ...prev, [name]: value };
      }
    });

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['tenant', 'contrast', 'dateOfBilling', 'referringPhysicianName'];

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')} is required`;
      }
    });

    if (formData.emergency) {
      if (!formData.dateOfEmergency) {
        newErrors.dateOfEmergency = 'Date of Emergency is required';
      }
    } else {
      if (!formData.mobileNumber) {
        newErrors.mobileNumber = 'Mobile number is required';
      } else if (formData.mobileNumber.length !== 10) {
        newErrors.mobileNumber = 'Mobile number must be 10 digits';
      }
    }
    if (!formData.payMode && !disabledSchemes.includes(selectedPatient?.scheme)) {
      newErrors.payMode = 'Please select a pay mode';
    }

    if (!selectedPatient) {
      newErrors.patientName = 'Please select a patient';
    }

    formData.studies.forEach((study, index) => {
      if (!study.study) {
        newErrors[`studies.${index}.study`] = 'Please select a study';
      }
      if (study.discount !== '' && (isNaN(study.discount) || study.discount < 0 || study.discount > 100)) {
        newErrors[`studies.${index}.discount`] = 'Discount must be a number between 0 and 100';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePrint = async () => {
    setLoading(true);
    try {
      const htmlContent = billingTableHtml();

      const printWindow = window.open('', '', 'width=800,height=600');
      printWindow.document.write('<html><head><title>Print</title>');
      printWindow.document.write('<style>body { font-family: Arial, sans-serif; }</style>');
      printWindow.document.write('</head><body>');
      printWindow.document.write(htmlContent);
      printWindow.document.write('</body></html>');
      printWindow.document.close();

      printWindow.onload = function () {
        printWindow.print();
        printWindow.close();
      };
    } catch (error) {
      console.error('Error printing:', error);
      alert('Failed to print. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (print) {
      const timer = setTimeout(() => {
        handlePrint();
        setFormData({
          patientName: '',
          tenant: '',
          scheme: '',
          schemeCode: '',
          payMode: '',
          contrast: '',
          dateOfBilling: new Date().toISOString().split('T')[0],
          mobileNumber: '',
          referringPhysicianName: '',
          studies: [{ study: null, studyDescription: '', studyId: '', amount: '', discount: '' }],
        });
        setErrors({});
        setValidated(false);
        setSelectedPatient(null);
        setSelectedItem(null);
        setPrint(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [print]);

  const padZero = (num) => (num < 10 ? `0${num}` : num);

  const regDate = new Date(selectedPatient?.regDate);

  const visitDate = `${regDate.getFullYear()}-${padZero(regDate.getMonth() + 1)}-${padZero(
    regDate.getDate()
  )} ${padZero(regDate.getHours())}:${padZero(regDate.getMinutes())}:${padZero(regDate.getSeconds())}`;

  useEffect(() => {
    const convertToBase64 = (image) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBase64Image(reader.result);
      };
      reader.readAsDataURL(image);
    };

    fetch(logoInvoice)
      .then((response) => response.blob())
      .then((blob) => {
        convertToBase64(blob);
      })
      .catch((error) => console.error('Error converting image to base64:', error));
  }, []);

  const billingTableHtml = () => {
    return `
    <div style="max-width: 800px; margin: 0 auto; font-family: Arial, sans-serif; font-size: 14px; color: #000;">
      <div style="text-align: center;">
        <img src="${base64Image}" alt="Siddharth Logo" style="width: 500px;" />
        <h1 style="font-size: 22px; font-weight: bold; text-align: center; border-bottom: 1px solid #000;">INVOICE</h1>
        <h2 style="font-size: 18px; font-weight: bold; text-align: center;">${currentUser?.name}</h2>
      </div>

      <div style="width: 100%; border-bottom: 2px solid #000; margin-bottom: 20px;">
      <div style="display: flex; justify-content: space-between; width: 100%;">
        <div style="width: 50%;">
          <div style="display: flex; margin-bottom: 10px;">
            <span style="text-align: left; width: 30%;">Patient No.</span>
            <span style="width: 10%; ext-align: center;">:</span>
            <strong style="width: 60%;">${selectedPatient.patientNumber}</strong>
          </div>
    
          <div style="display: flex; margin-bottom: 10px;">
            <span style="text-align: left; width: 30%;">Patient</span>
            <span style="width: 10%; ext-align: center;">:</span>
            <strong style="width: 60%;">
            ${selectedPatient?.title}. ${selectedPatient?.firstName} ${selectedPatient?.lastName} (${
      selectedPatient?.age
    } | ${selectedPatient?.gender})
            </strong>
          </div>
    
          <div style="display: flex; margin-bottom: 10px;">
            <span style="text-align: left; width: 30%;">Mobile</span>
            <span style="width: 10%; ext-align: center;">:</span>
            <span style="width: 60%;">${selectedPatient?.mobile}</span>
          </div>
    
          <div style="display: flex; margin-bottom: 10px;">
            <span style="text-align: left; width: 30%;">Referred by</span>
            <span style="width: 10%; ext-align: center;">:</span>
            <span style="width: 60%;">${formData.referringPhysicianName}</span>
          </div>
    
          <div style="display: flex; margin-bottom: 10px;">
            <span style="text-align: left; width: 30%;">IPD</span>
            <span style="width: 10%; ext-align: center;">:</span>
            <span style="width: 60%;">${selectedPatient.opdIpdNo}</span>
          </div>
        </div>
    
        <div style="width: 50%;">
        <div style="display: flex; margin-bottom: 10px;">
            <span style="text-align: left; width: 30%;">Bill No / Lab No</span>
            <span style="width: 10%; ext-align: center;">:</span>
            <strong style="width: 60%;">${billId}</strong>
          </div>
    
          <div style="display: flex; margin-bottom: 10px;">
            <span style="text-align: left; width: 30%;">Visit Date</span>
            <span style="width: 10%; ext-align: center;">:</span>
            <span style="width: 60%;">${visitDate}</span>
          </div>
    
          <div style="display: flex; margin-bottom: 10px;">
            <span style="text-align: left; width: 30%;">Print Date</span>
            <span style="width: 10%; ext-align: center;">:</span>
            <span style="width: 60%;">${formData.dateOfBilling} ${currentTime}</span>
          </div>
    
          <div style="display: flex; margin-bottom: 10px;">
            <span style="text-align: left; width: 30%;">Bill User</span>
            <span style="width: 10%; ext-align: center;">:</span>
            <span style="width: 60%;">${currentUser?.name}</span>
          </div>
        </div>
      </div>
    </div>
    
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
        <thead>
          <tr>
            <th style="padding: 10px; text-align: left; border-bottom: 1px solid #000;">SI.No</th>
            <th style="padding: 10px; text-align: left; border-bottom: 1px solid #000;">InvestId</th>
            <th style="padding: 10px; text-align: left; border-bottom: 1px solid #000;">Description</th>
            <th style="padding: 10px; text-align: right; border-bottom: 1px solid #000;">Amount</th>
          </tr>
        </thead>
        <tbody>
        ${formData.studies
          .map(
            (study, i) => `
          <tr>
          <td style="padding: 10px; border-bottom: 1px solid #000; text-align: left;">${i + 1}</td>
   
          <td style="padding: 10px; border-bottom: 1px solid #000; text-align: left;">
          ${study.study ? study.study.id : 'No study selected.'}
            </td>                
                  
          <td style="padding: 10px; border-bottom: 1px solid #000; text-align: left;">
          ${study.study ? study.study.description : 'No study selected.'}
            </td>                
                  
            <td style="padding: 10px; border-bottom: 1px solid #000; text-align: right;">${
              study?.amount
            }</td>           
          </tr>
        `
          )
          .join('')}
      </tbody>
      </table>

      <div style="display: flex; justify-content: flex-end;">
        <table style="border-collapse: collapse;">
        <tbody>
        <tr>
          <td style="font-weight: bold; padding-bottom: 10px; padding-right: 20px;">Total Amount :</td>
          <td style="font-weight: bold; text-align: right; padding-bottom: 10px;">${calculateTotal()}</td>
        </tr>
        <tr>
          <td style="font-weight: bold; padding-bottom: 10px; padding-right: 20px;">Amount Received :</td>
          <td style="font-weight: bold; text-align: right; padding-bottom: 10px;">
          ${!disabledSchemes.includes(selectedPatient?.scheme) ? calculateTotal() : '0.00'}
          </td>
        </tr>
        <tr>
          <td style="font-weight: bold; padding-bottom: 10px; padding-right: 20px;">Credit Amount :</td>
          <td style="font-weight: bold; text-align: right; padding-bottom: 10px;">
          ${disabledSchemes.includes(selectedPatient?.scheme) ? calculateTotal() : '0.00'}</td>
        </tr>
      </tbody>      
        </table>
      </div>

      <div style="text-decoration: underline; border-top: 2px solid #000; font-style: italic; margin-bottom: 20px;">
        Payment Details:
      </div>
      <div style="display: flex; justify-content: space-between;">
      <div style="display: flex; flex-direction: column">
        <strong>Paid Date</strong>
        <strong>${formData.dateOfBilling}</strong>
        </div>
        <div style="display: flex; flex-direction: column">
        <strong>Paid Amount</strong>
        <strong>${calculateTotal()}</strong>
        </div>
        <div style="display: flex; flex-direction: column">
        <strong>Paid Mode</strong>
        <strong>${
          disabledSchemes.includes(selectedPatient?.scheme) ? selectedPatient?.scheme : formData.payMode
        }</strong>
      </div>
      </div>

      <div style="margin-top: 20px; margin-bottom: 20px; line-height: 1.8;">
      <p>
        <strong>
          Note: Reports will be dispatched during working hours. Report will take 6 hrs to dispatch from the time of
          scan completion. Emergency cases will be dispatched in 2 hrs from the time of scan completion.
        </strong>
      </p>
      <p style="margin-top: 20px; margin-bottom: 20px;">
      <strong>For Siddhart MRI and CT SCAN Services Private Limited.</strong>
      </p>
      <p>
        <strong>Authorized Signatory</strong>
      </p>
      <p>
        <strong>Corp. Office:</strong>
        2nd Floor 4-A, Green Nagar, Durgapura, Jaipur (Raj.) - 302018 T: 0141-4917068, 9782427272
      </p>
    </p>    
    </div>
  `;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    if (validateForm()) {
      setLoading(true);
      try {
        await postDatatoServer({
          end_point: `add-bill-details`,
          body: {
            regDate: visitDate,
            dateOfBilling: `${formData.dateOfBilling} ${currentTime}`,
            payMode: disabledSchemes.includes(selectedPatient?.scheme) ? selectedPatient?.scheme : formData.payMode,
            contrast: formData.contrast,
            schemeCode: selectedPatient.schemeCode,
            mobileNumber: `+91` + selectedPatient.mobile,
            remarks: formData.remarks,
            scheme: selectedPatient.scheme,
            tenantType: formData.tenant,
            tenant: currentUser?.name,
            patientGender: selectedPatient.gender,
            dateOfBirth: selectedPatient.dob,
            weight: selectedPatient.weight,
            patientAge: selectedPatient.age,
            patientAddress: `${selectedPatient.street}, ${selectedPatient.city}, ${selectedPatient.state}`,
            patientName: `${selectedPatient.title} ${selectedPatient.firstName} ${selectedPatient.lastName}`,
            referringPhysicianName: formData.referringPhysicianName,
            studies: formData.studies.map((study) => ({
              studyId: study.study.id,
              studyDescription: study.studyDescription,
              unitPrice: study.amount,
              discount: study.discount,
              study: study.study.description,
            })),
          },
          call_back: (res) => {
            if (res.response.status) {
              toast.success(res.response.message);
              setBillId(res.response.billNumber);
              setPrint(true);
            } else {
              toast.error(res.response.message);
            }
          },
          props: { header: true, token: currentUser?.token },
        });
      } catch (error) {
        console.error('Error generating bill:', error);
        toast.error('Failed to generate bill.');
      } finally {
        setLoading(false);
      }
    }
  };

  const addStudy = () => {
    const lastSubStudy = formData.studies[formData.studies.length - 1];
    if (!lastSubStudy.study || !lastSubStudy.amount) {
      toast.error('Please complete the current study before adding a new one.');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      studies: [...prev.studies, { study: null, studyDescription: '', studyId: '', amount: '', discount: '' }],
    }));
  };

  const removeStudy = (index) => {
    setFormData((prev) => ({
      ...prev,
      studies: prev.studies.filter((_, i) => i !== index),
    }));
  };

  const calculateTotal = () => {
    return formData.studies
      .reduce((total, study) => {
        const amount = parseFloat(study.amount) || 0;
        const discount = parseFloat(study.discount) || 0;
        const discountAmount = (amount * discount) / 100;
        return total + (amount - discountAmount);
      }, 0)
      .toFixed(2);
  };

  return (
    <>
      {show && <PatientRegistrationModal handleClose={() => setShow(false)} fetchPatientData={fetchPatientData} />}
      {(billingView && <BillingReports handleOpen={() => setBillingView(false)} />) || (
        <>
          <Container fluid className="px-0">
            <div className="bg-white p-4 min-vh-100 shadow-sm">
              <div className="d-flex justify-content-between">
                <h3 className="base-color mb-2">{(billingView && 'Billing Details') || 'Patient Billing'}</h3>
                <div>
                  <Button
                    variant=""
                    className="login-btn fs-15 base-color-bg text-white fw-semibold"
                    onClick={() => setBillingView(true)}
                  >
                    Billing Reports
                  </Button>
                </div>
              </div>

              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Row>
                  <Col md={12} className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="emr-checkbox"
                      label="Emergency"
                      name="emergency"
                      checked={formData.emergency}
                      onChange={(e) => {
                        const isEmergency = e.target.checked;
                        setFormData((prevData) => ({
                          ...prevData,
                          emergency: isEmergency,
                          mobileNumber: isEmergency ? '' : prevData.mobileNumber,
                          dateOfEmergency: isEmergency ? '' : prevData.dateOfEmergency,
                        }));
                        if (isEmergency) {
                          setSelectedPatient(null);
                          setPatientUsers([]);
                        }
                        setErrors({});
                      }}
                    />
                  </Col>
                  <FormField
                    label="Tenant"
                    name="tenant"
                    value={formData.tenant}
                    onChange={(e) => {
                      handleChange(e);
                      setFormData((prev) => ({
                        ...prev,
                        studies: [{ study: null, studyDescription: '', studyId: '', amount: '', discount: '' }],
                      }));
                    }}
                    error={errors.tenant}
                    required
                    options={['Private', 'Government']}
                  />
                  {formData.emergency ? (
                    <FormField
                      label="Date of Emergency"
                      name="dateOfEmergency"
                      value={formData.dateOfEmergency}
                      onChange={handleChange}
                      error={errors.dateOfEmergency}
                      type="date"
                      required
                    />
                  ) : (
                    <Form.Group as={Col} md={3} className="mb-3">
                      <Form.Label>
                        Mobile Number
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text>{`+91`}</InputGroup.Text>
                        <Form.Control
                          type="tel"
                          name="mobileNumber"
                          isInvalid={errors.mobileNumber}
                          required={true}
                          value={formData.mobileNumber}
                          onChange={handleMobileChange}
                          placeholder="Enter 10 digit number"
                          maxLength={10}
                        />
                      </InputGroup>
                      {errors.mobileNumber && <div className="text-danger fs-13">{errors.mobileNumber}</div>}
                    </Form.Group>
                  )}
                  <div className="col-md-3">
                    <Form.Label className="base-color cursor-pointer" onClick={() => setShow(true)}>
                      Patient
                      <span className="text-danger">*</span>
                    </Form.Label>
                    {patientUsers && (
                      <SearchableDropdown
                        items={patientUsers}
                        onSelect={setSelectedPatient}
                        selectedItem={selectedPatient}
                        displayKey={displayPatient}
                        setSelectedItem={setSelectedItem}
                        searchKeys={['firstName', 'lastName']}
                        placeholder="Select Patient"
                        searchPlaceholder="Search by Patient Name"
                        noItemsText="No patients found"
                      />
                    )}
                    {errors.patientName && <div className="text-danger fs-13">{errors.patientName}</div>}
                  </div>

                  <FormField
                    label="Contrast Type"
                    name="contrast"
                    value={formData.contrast}
                    onChange={(e) => {
                      handleChange(e);
                      setFormData((prev) => ({
                        ...prev,
                        studies: [{ study: null, studyDescription: '', studyId: '', amount: '', discount: '' }],
                      }));
                    }}
                    error={errors.contrast}
                    required
                    options={['Plain', 'Contrast']}
                  />
                  {formData.studies.map((study, index) => (
                    <React.Fragment key={index}>
                      <div className="col-md-3">
                        <Form.Label>
                          Study {index + 1}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        {studyData && (
                          <SearchableDropdown
                            items={getFilteredStudies()}
                            onSelect={(selected) => handleStudySelect(selected, index)}
                            selectedItem={study.study}
                            setSelectedItem={setSelectedItem}
                            displayKey={displayStudy}
                            searchKeys={['description', 'code']}
                            placeholder="Select Study"
                            searchPlaceholder="Search by Study Name or Code"
                            noItemsText="No study found"
                          />
                        )}
                        {errors[`studies.${index}.study`] && (
                          <div className="text-danger fs-13">{errors[`studies.${index}.study`]}</div>
                        )}
                      </div>

                      <Form.Group as={Col} lg={3} md={4} sm={6} className="mb-3">
                        <Form.Label>Study Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          name={`studies.studyDescription`}
                          value={study.studyDescription}
                          onChange={(e) => handleChange(e, index)}
                        />
                      </Form.Group>

                      <Form.Group as={Col} md={3} className="mb-3">
                        <Form.Label>Unit Price</Form.Label>
                        <Form.Control
                          type="text"
                          name={`studies.${index}.amount`}
                          value={study.amount ? parseFloat(study.amount).toFixed(2) : '0.00'}
                          onChange={(e) => handleChange(e, index)}
                          disabled={true}
                        />
                      </Form.Group>

                      <Form.Group as={Col} md={2} className="mb-3">
                        <Form.Label>Discount %</Form.Label>
                        <Form.Control
                          type="text"
                          name={`studies.discount`}
                          value={study.discount}
                          onChange={(e) => handleChange(e, index)}
                        />
                        {errors[`studies.${index}.discount`] && (
                          <div className="text-danger fs-13">{errors[`studies.${index}.discount`]}</div>
                        )}
                      </Form.Group>

                      {index === 0 && (
                        <Col md={1}>
                          <div className="mt-4">
                            <Button className="add-btn py-1 px-2 mx-2 mt-1" onClick={addStudy}>
                              <Add sx={{ fontSize: '24px' }} />
                            </Button>
                          </div>
                        </Col>
                      )}

                      {index > 0 && (
                        <Col md={1}>
                          <div className="mt-4">
                            <Button variant="outline-danger" onClick={() => removeStudy(index)}>
                              <Delete sx={{ fontSize: '24px' }} />
                            </Button>
                          </div>
                        </Col>
                      )}
                    </React.Fragment>
                  ))}
                  <FormField
                    label="Referring Physician Name"
                    name="referringPhysicianName"
                    value={formData?.referringPhysicianName
                      ?.replace(/^\s+/, '')
                      .replace(/\s{2,}/g, ' ')
                      .trimStart()}
                    onChange={handleChange}
                    error={errors.referringPhysicianName}
                    required
                  />

                  {(!disabledSchemes.includes(selectedPatient?.scheme) && (
                    <FormField
                      label="Pay Mode"
                      name="payMode"
                      value={formData.payMode}
                      onChange={handleChange}
                      error={errors.payMode}
                      required
                      options={['Cash', 'Online', 'Credit Card', 'Debit Card', 'Cheque', 'UPI']}
                    />
                  )) || (
                    <FormField
                      label="Pay Mode"
                      name="payMode"
                      value={selectedPatient?.scheme}
                      onChange={handleChange}
                      error={errors.payMode}
                      required
                      disabled={true}
                    />
                  )}
                  <FormField
                    label="Date of Billing"
                    name="dateOfBilling"
                    value={formData.dateOfBilling}
                    onChange={handleChange}
                    error={errors.dateOfBilling}
                    type="date"
                  />

                  <Form.Group as={Col} lg={3} md={4} sm={6} className="mb-3">
                    <Form.Label>Remarks</Form.Label>
                    <Form.Control as="textarea" name="remarks" value={formData.remarks} onChange={handleChange} />
                  </Form.Group>
                </Row>
                <Table bordered responsive className="mb-4">
                  <thead className="base-color-bg text-white text-nowrap">
                    <tr>
                      <th>Patient Details</th>
                      <td>Billing Date</td>
                      <th>Referring Physician Name</th>
                      {formData.remarks && <td>Remarks</td>}
                      {formData.scheme && <td>Scheme</td>}
                      <th>Study</th>
                      <th>Study Description</th>
                      <th>Unit Price</th>
                      <th>Discount</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.studies.map((study, index) => (
                      <tr key={index}>
                        {index === 0 && (
                          <>
                            <td rowSpan={formData.studies.length}>
                              {selectedPatient && (
                                <>
                                  <div>
                                    Full Name: {selectedPatient.title}. {selectedPatient.firstName}{' '}
                                    {selectedPatient.lastName}
                                  </div>
                                  <div>Mobile: {selectedPatient.mobile}</div>
                                </>
                              )}
                            </td>
                            <td rowSpan={formData.studies.length}>
                              {moment(formData.dateOfBilling).format('DD-MMM-YYYY')}
                            </td>
                          </>
                        )}
                        {index === 0 && <td rowSpan={formData.studies.length}>{formData.referringPhysicianName}</td>}
                        {index === 0 && formData.remarks && (
                          <td rowSpan={formData.studies.length}>{formData.remarks}</td>
                        )}
                        {index === 0 && formData.scheme && (
                          <td rowSpan={formData.studies.length}>
                            {formData.scheme} {formData.schemeCode}
                          </td>
                        )}
                        <td>{study.study ? study.study.description : ''}</td>
                        <td>{study.studyDescription}</td>
                        <td>{study.amount}</td>
                        <td>{(study.discount && study.discount + '%') || ''}</td>
                        <td>
                          {(study.amount.length !== 0 &&
                            (
                              (parseFloat(study.amount) || 0) -
                              ((parseFloat(study.amount) || 0) * (parseFloat(study.discount) || 0)) / 100
                            ).toFixed(2)) ||
                            ''}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td
                        colSpan={formData.scheme && formData.remarks ? 9 : formData.scheme || formData.remarks ? 8 : 7}
                        className="text-end"
                      >
                        <strong>Grand Total</strong>
                      </td>

                      <td>
                        <strong>{calculateTotal()}</strong>
                      </td>
                    </tr>
                  </tbody>
                </Table>

                <Button type="submit" className="login-btn base-color-bg text-white px-4" disabled={loading}>
                  {loading ? 'Printing...' : 'Print Bill'}
                </Button>
              </Form>
            </div>
          </Container>
        </>
      )}
    </>
  );
};

export default PatientBilling;
